<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useQuizterStore } from '../../stores/quizterStore';
import Questions from '../../components/Questions.vue';
import Card from '../../components/Card.vue';
import { answersToPercentage } from '../../util/statistics';
import NextButton from '../../components/NextButton.vue';
import useLocale from '../../../composables/use-locale';
import FinalQuestionAnimation from '../../components/host/FinalQuestionAnimation.vue';

const quizterStore = useQuizterStore();
const {
  currentQuestionAnswer,
  currentQuestion,
  currentQuestionIndex,
  numberOfQuestions,
  questionAnswers,
  isLoading,
  gameOptions,
  isGameFinished,
  returnParams,
  joinedPlayers,
} = storeToRefs(quizterStore);

const { t } = useLocale();

const currentQuestionStatistics = computed(() => {
  return questionAnswers.value[currentQuestionIndex.value]
    ? answersToPercentage(questionAnswers.value[currentQuestionIndex.value], joinedPlayers.value.length)
    : {};
});

const isNextQuestionFinal = computed(
  () => !!numberOfQuestions.value && currentQuestionIndex.value === numberOfQuestions.value - 2,
);

const showFinalQuestionAnimation = ref(false);

const handleNextQuestion = () => {
  if (isNextQuestionFinal.value) {
    showFinalQuestionAnimation.value = true;
  } else {
    goToNextQuestion();
  }
};

const showLeaderboardView = () => {
  quizterStore.showResults();
};

const goToNextQuestion = () => {
  if (gameOptions.value.useQuestionShowTimer) {
    quizterStore.nextQuestion();
  } else {
    quizterStore.showQuestionPhrase();
  }
};
</script>

<template>
  <div class="d-flex flex-column align-center ga-6">
    <!-- Animation takes over the whole area (no card, no buttons) -->
    <FinalQuestionAnimation v-if="showFinalQuestionAnimation" @complete="goToNextQuestion" />

    <!-- Normal content: Card + buttons (hidden during animation) -->
    <template v-else>
      <Card :content-max-width="800">
        <Questions
          v-if="currentQuestion"
          :currentQuestion="currentQuestion"
          :key="`question-${currentQuestionIndex}`"
          :currentQuestionIndex="currentQuestionIndex"
          :numberOfQuestions="numberOfQuestions"
          :highlight-correct-answer="true"
          :current-question-answer="currentQuestionAnswer"
          :current-question-answers="currentQuestionStatistics"
          :host="true"
          :showTimer="false"
        />
      </Card>

      <!-- With scoring: show leaderboard button -->
      <template v-if="gameOptions.useScoring">
        <NextButton
          size="large"
          @click="showLeaderboardView"
          :loading="isLoading"
          data-testid="quizter-show-leaderboard-button"
        >
          {{
            currentQuestionIndex === numberOfQuestions! - 1
              ? t('results.button_show_final_result')
              : t('results.button_show_leaderboard')
          }}
        </NextButton>
      </template>

      <!-- Without scoring: show next question or return button -->
      <template v-else>
        <NextButton v-if="!isGameFinished" size="large" @click="handleNextQuestion" :loading="isLoading">
          {{ isNextQuestionFinal ? t('results.final_question_announcement') : t('results.button_next_question') }}
        </NextButton>
        <NextButton
          v-else-if="returnParams"
          @click="quizterStore.reset"
          :href="returnParams.url"
          data-testid="quizter-return-button"
        >
          {{ returnParams.text }}
        </NextButton>
      </template>
    </template>
  </div>
</template>
